/**
 * planFeatures.js — Plan-feature gating helper.
 *
 * Reads `onboardingStatus.plan_features` (extended by backend B1.7) to decide
 * whether to show/lock new dashboard surfaces.
 *
 * Falls back to `false` for every feature if the flag is missing — safe-by-default.
 *
 * Expected shape from /company/onboarding-status:
 *   plan_features: {
 *     transactional_api:               boolean,
 *     audience_byo_webhook:            boolean,
 *     whatsapp_transactional_limit:    number,
 *     whatsapp_transactional_used:     number,
 *   }
 */

import { useOnboarding } from '../context/onboardingContext';

const DEFAULT_FEATURES = {
  transactional_api: false,
  audience_byo_webhook: false,
  whatsapp_transactional_limit: 0,
  whatsapp_transactional_used: 0,
};

/**
 * Plan-name → feature derivation (fallback when backend omits plan_features).
 * Keep in sync with backend plan catalogue.
 */
const PLAN_DEFAULTS = {
  ENTERPRISE: {
    transactional_api: true,
    audience_byo_webhook: true,
    whatsapp_transactional_limit: -1, // unlimited
  },
  PRO: {
    transactional_api: true,
    audience_byo_webhook: true,
    whatsapp_transactional_limit: 50000,
  },
  BUSINESS: {
    transactional_api: true,
    audience_byo_webhook: false,
    whatsapp_transactional_limit: 10000,
  },
  STARTER: {
    transactional_api: false,
    audience_byo_webhook: false,
  },
  FREE: {
    transactional_api: false,
    audience_byo_webhook: false,
  },
};

const featuresFromPlanName = (planName) => {
  if (!planName) return {};
  const key = String(planName).trim().toUpperCase();
  return PLAN_DEFAULTS[key] || {};
};

/**
 * useFeature(name)  →  boolean
 * useFeature()      →  full plan_features object (with defaults)
 */
/**
 * Boolean feature flags — for these we OR plan-name defaults with backend values
 * so a known paid plan never gets locked out by a backend that forgot to set the flag.
 */
const BOOLEAN_FLAGS = ['transactional_api', 'audience_byo_webhook', 'outbound_webhooks'];

export const usePlanFeatures = () => {
  const { onboardingStatus, loadingOnboarding } = useOnboarding();
  const fromPlanName = featuresFromPlanName(onboardingStatus?.plan_name);
  const fromBackend = onboardingStatus?.plan_features || {};

  // Start with defaults, then layer plan-name, then backend.
  const merged = {
    ...DEFAULT_FEATURES,
    ...fromPlanName,
    ...fromBackend,
  };

  // For boolean flags: if EITHER plan-name defaults OR backend says true, it's true.
  // Prevents a known paid plan from being locked when backend omits / mis-reports the flag.
  BOOLEAN_FLAGS.forEach((k) => {
    merged[k] = Boolean(fromPlanName[k]) || Boolean(fromBackend[k]);
  });

  // For numeric quotas: prefer backend value when it's a real number, else fall back to plan-name.
  if (
    fromBackend.whatsapp_transactional_limit === null ||
    fromBackend.whatsapp_transactional_limit === undefined
  ) {
    merged.whatsapp_transactional_limit =
      fromPlanName.whatsapp_transactional_limit ?? DEFAULT_FEATURES.whatsapp_transactional_limit;
  }

  return { features: merged, loading: loadingOnboarding, planName: onboardingStatus?.plan_name || null };
};

export const useFeatureFlag = (flagName) => {
  const { features, loading } = usePlanFeatures();
  return { enabled: Boolean(features[flagName]), loading };
};

export const useTransactionalQuota = () => {
  const { features, loading } = usePlanFeatures();
  const limit = Number(features.whatsapp_transactional_limit || 0);
  const used = Number(features.whatsapp_transactional_used || 0);
  const remaining = Math.max(0, limit - used);
  const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return { limit, used, remaining, percent, loading, isUnlimited: limit === -1 };
};

export const FEATURE_KEYS = {
  TRANSACTIONAL_API: 'transactional_api',
  BYO_WEBHOOK: 'audience_byo_webhook',
};
